import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { guideAPI } from '../api';
import { getTranslation } from '../i18n';

const TONE = {
  page: 'var(--kahla-cream, #faf8f5)',
  text: 'var(--kahla-charcoal, #4a4540)',
  textLight: 'var(--kahla-text-light, #8a847b)',
  border: 'var(--kahla-border, #ece9e4)',
};

/* Convert a YouTube/Vimeo URL into an embeddable iframe URL.
   Falls back to the original URL if not recognised. */
function toEmbedUrl(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('youtu.be')) {
      return `https://www.youtube.com/embed/${u.pathname.replace('/', '')}`;
    }
    if (u.hostname.includes('youtube.com')) {
      const id = u.searchParams.get('v');
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (u.hostname.includes('vimeo.com')) {
      return `https://player.vimeo.com/video/${u.pathname.replace('/', '')}`;
    }
  } catch (e) {}
  return url;
}

const Section = ({ s, t }) => {
  const [open, setOpen] = useState(false);
  const embed = toEmbedUrl(s.video_url);
  return (
    <article
      data-testid={`guide-section-${s.id}`}
      style={{
        borderBottom: `1px solid ${TONE.border}`,
        padding: '36px 0',
      }}
    >
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          width: '100%',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 18,
        }}
      >
        <span style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 28,
          color: TONE.text,
          opacity: 0.4,
          fontWeight: 300,
          lineHeight: 1,
          marginTop: 4,
          minWidth: 24,
          transition: 'transform 0.25s ease',
          transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
        }}>+</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          {s.recommended_for && s.recommended_for.length > 0 && (
            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: TONE.textLight,
              margin: '0 0 8px',
              fontWeight: 500,
            }}>
              {t.guide_recommended_for} {s.recommended_for.join(' · ')}
            </p>
          )}
          <h3 style={{
            fontFamily: 'Playfair Display, serif',
            fontSize: 24,
            fontWeight: 400,
            color: TONE.text,
            margin: 0,
            lineHeight: 1.25,
          }}>
            {s.title}
          </h3>
          {s.intro && (
            <p style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 15,
              color: TONE.textLight,
              margin: '8px 0 0',
              lineHeight: 1.7,
            }}>
              {s.intro}
            </p>
          )}
        </div>
      </button>

      {open && (
        <div style={{
          padding: '24px 0 0 42px',
          maxWidth: 760,
          fontFamily: 'Inter, sans-serif',
          fontSize: 14,
          color: TONE.text,
          lineHeight: 1.85,
        }}>
          {s.image_url && (
            <img
              src={s.image_url}
              alt=""
              style={{
                width: '100%',
                maxHeight: 460,
                objectFit: 'cover',
                marginBottom: 24,
                border: `1px solid ${TONE.border}`,
              }}
            />
          )}
          {embed && (
            <div style={{
              position: 'relative',
              paddingTop: '56.25%',
              marginBottom: 24,
              border: `1px solid ${TONE.border}`,
              background: '#000',
            }}>
              <iframe
                src={embed}
                title={s.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  position: 'absolute', inset: 0,
                  width: '100%', height: '100%', border: 0,
                }}
              />
            </div>
          )}
          {s.body && s.body.split('\n').map((line, i) => (
            line.trim() === '' ? <br key={i} /> : (
              <p key={i} style={{ margin: '0 0 14px' }}>{line}</p>
            )
          ))}
        </div>
      )}
    </article>
  );
};

const MaestraGuide = () => {
  const navigate = useNavigate();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const t = getTranslation('en');

  useEffect(() => {
    document.title = 'Maestra Guide';
    guideAPI.list()
      .then(res => setSections(res.data.sections || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: TONE.page }}>
      <header style={{
        padding: '36px 48px 28px',
        maxWidth: 1240,
        margin: '0 auto',
        borderBottom: `1px solid ${TONE.border}`,
      }}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          data-testid="guide-back-btn"
          style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 12, letterSpacing: '0.04em',
            color: TONE.textLight, fontFamily: 'Inter, sans-serif',
          }}
        >
          ← Back
        </button>
      </header>

      <section style={{ maxWidth: 920, margin: '0 auto', padding: '64px 48px 96px' }}>
        <p style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 11,
          letterSpacing: '0.32em',
          textTransform: 'uppercase',
          color: TONE.textLight,
          margin: 0,
          fontWeight: 300,
        }}>
          {t.guide_nav_label}
        </p>
        <h1 style={{
          fontFamily: 'Playfair Display, serif',
          fontSize: 'clamp(34px, 5vw, 52px)',
          fontWeight: 400,
          color: TONE.text,
          margin: '18px 0 24px',
          lineHeight: 1.1,
          letterSpacing: '-0.005em',
        }}>
          A studio companion.
        </h1>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: 18,
          lineHeight: 1.7,
          color: TONE.textLight,
          margin: '0 0 64px',
          maxWidth: 640,
        }}>
          {t.guide_intro}
        </p>

        {loading ? (
          <p style={{ color: TONE.textLight, fontStyle: 'italic', fontSize: 14 }}>…</p>
        ) : sections.length === 0 ? (
          <p
            data-testid="guide-empty"
            style={{
              fontFamily: 'Cormorant Garamond, Playfair Display, serif',
              fontStyle: 'italic',
              fontSize: 16,
              color: TONE.textLight,
              padding: '64px 0',
              textAlign: 'center',
              border: `1px solid ${TONE.border}`,
              maxWidth: 560,
              margin: '0 auto',
            }}
          >
            {t.guide_empty_user}
          </p>
        ) : (
          <div data-testid="guide-sections-list">
            {sections.map(s => <Section key={s.id} s={s} t={t} />)}
          </div>
        )}
      </section>
    </div>
  );
};

export default MaestraGuide;
